import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { catchError, finalize, forkJoin, map, Observable, of, shareReplay } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ReportsService } from '../../../../reports.service';
import {
  areColumnsSynonymous,
  buildJoinTypeOptions,
  buildPrimaryKeyMappingPairs,
  ColumnMapping,
  DatasetColumn,
  DatasetConfig,
  DatasetJoin,
  DatasourceOption,
  findDatasetColumn,
  findSynonymColumn,
  isDuplicateColumnAlias,
  isDuplicateColumnName,
  isJoinFullyConfigured,
  JoinTypeOption,
  mapApiColumn,
  validateCalculatedColumnExpression
} from '../select-datasource-modal.model';

let joinSequence = 0;
let columnMappingSequence = 0;

// Column fetches in flight, keyed by dataset. Module-level so every card (and the auto-join logic that
// reaches into other datasets) shares one request per dataset and can wait for it to finish.
const columnLoads = new WeakMap<DatasetConfig, Observable<void>>();

@Component({
  selector: 'app-dataset-card',
  standalone: false,
  templateUrl: './dataset-card.component.html',
  styleUrl: './dataset-card.component.scss'
})
export class DatasetCardComponent {
  @Input() dataset!: DatasetConfig;
  @Input() index = 0;
  @Input() allDatasets: DatasetConfig[] = [];
  @Input() datasourceOptions: DatasourceOption[] = [];
  @Input() usedTableNames: string[] = [];
  @Input() canRemove = false;

  @Output() remove = new EventEmitter<void>();
  @Output() configChanged = new EventEmitter<void>();
  @Input() hasReportAdminRole: boolean = false;

  @ViewChild('calculatedColumnModal') private calculatedColumnModal: any;

  calculatedColumnName = '';
  calculatedColumnExpression = '';
  calculatedColumnNameError: string | null = null;
  calculatedColumnExpressionError: string | null = null;
  calculatedColumnSaving = false;
  calculatedColumnPickerOpen = false;
  calculatedColumnSelectedKeys = new Set<string>();
  // Columns actually inserted into the expression so far (accumulated across multiple inserts) - kept
  // separate from calculatedColumnSelectedKeys so the picker checkboxes can clear after each insert
  // while the saved column's description still reflects every column that was combined.
  private calculatedColumnInsertedKeys = new Set<string>();

  constructor(private reportsService: ReportsService, private toastr: ToastrService) {}

  onDatasourceSelected(tableName: string): void {
    this.dataset.table_name = tableName;
    const matched = this.datasourceOptions.find(option => option.table_name === tableName);
    this.dataset.dataset_name = matched?.dataset_name || '';
    this.dataset.table_description = matched?.table_description || '';
    this.dataset.Alias_Query = matched?.Alias_Query ?? null;
    this.dataset.business_rules = matched?.business_rules ?? null;
    this.dataset.is_restricted = !!matched?.is_restricted;
    this.dataset.restriction_condition = matched?.restriction_condition ?? null;
    this.dataset.columns = [];
    this.dataset.columnsLoaded = false;
    this.dataset.columnsLoading = false;
    columnLoads.delete(this.dataset);
    this.dataset.joins = [];
    this.dataset.relationshipsOpen = false;
    if (this.dataset.manageColumnsOpen) {
      this.ensureDatasetColumnsLoaded(this.dataset);
    }
    // Join to the primary dataset in the background so the user doesn't have to open "Establish relationships".
    if (!this.dataset.isPrimary) {
      this.addJoinToPrimaryDataset();
    }
    // Joins on other datasets that point at this one were built from its previous columns.
    this.refreshJoinsTargeting(this.dataset);
    this.configChanged.emit();
  }

  onDatasetNameChange(): void {
    this.configChanged.emit();
  }

  onToggleManageColumns(): void {
    if (!this.dataset.table_name) {
      return;
    }
    this.dataset.manageColumnsOpen = !this.dataset.manageColumnsOpen;
    if (this.dataset.manageColumnsOpen) {
      this.ensureDatasetColumnsLoaded(this.dataset);
    }
  }

  get allColumnsSelected(): boolean {
    return this.dataset.columns.length > 0 && this.dataset.columns.every(column => column.selected);
  }

  onUseAllColumns(): void {
    const shouldSelectAll = this.dataset.columns.some(column => !column.selected);
    this.dataset.columns.forEach(column => (column.selected = shouldSelectAll));
    this.configChanged.emit();
  }

  get availableJoinTargets(): DatasetConfig[] {
    return this.allDatasets.filter(dataset => dataset.id !== this.dataset.id);
  }

  // Each join targets one other dataset, so once every available target already has a join from
  // this dataset, there's nothing left to join to and "Add join" should no longer be offered.
  get canAddMoreJoins(): boolean {
    return this.dataset.joins.length < this.availableJoinTargets.length;
  }

  get relationshipsConfigured(): boolean {
    return this.dataset.joins.length > 0 && this.dataset.joins.every(join => this.isJoinConfigured(join));
  }

  isJoinConfigured(join: DatasetJoin): boolean {
    return isJoinFullyConfigured(join, this.dataset, this.allDatasets);
  }

  // Cached per join so the *ngFor array reference stays stable across change-detection cycles -
  // rebuilding a fresh array on every call here froze the panel (mat-select kept re-rendering its options).
  private joinTypeOptionsCache = new Map<string, { key: string; options: JoinTypeOption[] }>();

  getJoinTypeOptions(join: DatasetJoin): JoinTypeOption[] {
    const target = this.getDatasetById(join.targetDatasetId);
    const targetName = target?.dataset_name || target?.table_name || '';
    const currentName = this.dataset.dataset_name || this.dataset.table_name || '';
    const cacheKey = `${targetName}|${currentName}`;
    const cached = this.joinTypeOptionsCache.get(join.id);
    if (cached && cached.key === cacheKey) {
      return cached.options;
    }
    const options = buildJoinTypeOptions(targetName, currentName);
    this.joinTypeOptionsCache.set(join.id, { key: cacheKey, options });
    return options;
  }

  // Null when the pair is valid or incomplete; a message once both columns are picked but don't refer to the same field.
  getMappingError(join: DatasetJoin, mapping: ColumnMapping): string | null {
    if (!mapping.leftColumn || !mapping.rightColumn) {
      return null;
    }
    const target = this.getDatasetById(join.targetDatasetId);
    const leftColumn = findDatasetColumn(target, mapping.leftColumn);
    const rightColumn = findDatasetColumn(this.dataset, mapping.rightColumn);
    if (leftColumn && rightColumn && !areColumnsSynonymous(leftColumn, rightColumn)) {
      return `"${leftColumn.name}" and "${rightColumn.name}" don't appear to be the same field - map columns that represent the same data across tables.`;
    }
    return null;
  }

  getDatasetById(id: string | null): DatasetConfig | undefined {
    return this.allDatasets.find(dataset => dataset.id === id);
  }

  filterColumns(columns: DatasetColumn[] | undefined, search: string | undefined): DatasetColumn[] {
    const list = columns || [];
    const term = (search || '').trim().toLowerCase();
    if (!term) {
      return list;
    }
    return list.filter(column => column.name.toLowerCase().includes(term));
  }

  onToggleRelationships(): void {
    if (!this.dataset.table_name) {
      return;
    }
    this.dataset.relationshipsOpen = !this.dataset.relationshipsOpen;
    if (this.dataset.relationshipsOpen) {
      this.ensureDatasetColumnsLoaded(this.dataset);
      if (!this.dataset.joins.length) {
        this.onAddJoin();
      }
    }
  }

  trackByJoinId(_: number, join: DatasetJoin): string {
    return join.id;
  }

  trackByMappingId(_: number, mapping: ColumnMapping): string {
    return mapping.id;
  }

  onAddJoin(): void {
    if (!this.canAddMoreJoins) {
      return;
    }
    const join = this.createJoin();
    this.dataset.joins.push(join);
    this.autoConfigureJoin(this.dataset, join);
    this.configChanged.emit();
  }

  onRemoveJoin(join: DatasetJoin): void {
    this.dataset.joins = this.dataset.joins.filter(item => item.id !== join.id);
    this.joinTypeOptionsCache.delete(join.id);
    this.configChanged.emit();
  }

  // Picking a different table in the join rebuilds the join (LEFT JOIN + primary-key mappings) for that table.
  onJoinTargetChange(join: DatasetJoin): void {
    this.autoConfigureJoin(this.dataset, join);
    this.configChanged.emit();
  }

  onJoinTypeChange(join: DatasetJoin, joinType: DatasetJoin['joinType']): void {
    join.joinType = joinType;
    this.configChanged.emit();
  }

  onAddColumnMapping(join: DatasetJoin): void {
    join.columnMappings.push(this.createColumnMapping());
    this.configChanged.emit();
  }

  onRemoveColumnMapping(join: DatasetJoin, mapping: ColumnMapping): void {
    join.columnMappings = join.columnMappings.filter(item => item.id !== mapping.id);
    this.configChanged.emit();
  }

  // Auto-fills the OTHER side only while it's still empty, so a later change never bounces back
  // and overrides a column the user (or the earlier auto-select) already put in place.
  onLeftColumnChange(join: DatasetJoin, mapping: ColumnMapping): void {
    this.configChanged.emit();
    if (mapping.rightColumn) {
      return;
    }
    const leftColumn = findDatasetColumn(this.getDatasetById(join.targetDatasetId), mapping.leftColumn);
    const match = leftColumn ? findSynonymColumn(leftColumn, this.dataset.columns) : undefined;
    if (match) {
      mapping.rightColumn = match.key;
    }
  }

  onRightColumnChange(join: DatasetJoin, mapping: ColumnMapping): void {
    this.configChanged.emit();
    if (mapping.leftColumn) {
      return;
    }
    const targetDataset = this.getDatasetById(join.targetDatasetId);
    const rightColumn = findDatasetColumn(this.dataset, mapping.rightColumn);
    const match = rightColumn && targetDataset ? findSynonymColumn(rightColumn, targetDataset.columns) : undefined;
    if (match) {
      mapping.leftColumn = match.key;
    }
  }

  private createJoin(targetDatasetId: string | null = this.availableJoinTargets[0]?.id ?? null): DatasetJoin {
    joinSequence += 1;
    return {
      id: `join-${joinSequence}`,
      targetDatasetId,
      joinType: 'LEFT JOIN',
      columnMappings: [this.createColumnMapping()]
    };
  }

  private addJoinToPrimaryDataset(): void {
    const primary = this.allDatasets.find(dataset => dataset.isPrimary && dataset.id !== this.dataset.id);
    if (!primary?.table_name) {
      return;
    }
    const join = this.createJoin(primary.id);
    this.dataset.joins.push(join);
    this.autoConfigureJoin(this.dataset, join);
  }

  private refreshJoinsTargeting(target: DatasetConfig): void {
    this.allDatasets
      .filter(owner => owner.id !== target.id)
      .forEach(owner => owner.joins
        .filter(join => join.targetDatasetId === target.id)
        .forEach(join => this.autoConfigureJoin(owner, join)));
  }

  // Resets the join to LEFT JOIN with a blank mapping, then - once both tables' columns are available -
  // replaces it with one mapping per primary-key column that has a matching column on the other table.
  private autoConfigureJoin(owner: DatasetConfig, join: DatasetJoin): void {
    join.joinType = 'LEFT JOIN';
    join.columnMappings = [this.createColumnMapping()];

    const target = this.getDatasetById(join.targetDatasetId);
    if (!target) {
      return;
    }
    const targetId = target.id;
    const targetTable = target.table_name;
    const ownTable = owner.table_name;

    forkJoin([this.loadDatasetColumns(target), this.loadDatasetColumns(owner)]).subscribe(() => {
      // The user may have switched tables or removed the join while the columns were loading.
      const isStale = !owner.joins.includes(join)
        || join.targetDatasetId !== targetId
        || target.table_name !== targetTable
        || owner.table_name !== ownTable;
      if (isStale) {
        return;
      }
      const pairs = buildPrimaryKeyMappingPairs(target.columns, owner.columns);
      if (pairs.length) {
        join.columnMappings = pairs.map(pair => ({ ...this.createColumnMapping(), ...pair }));
      }
    });
  }

  private createColumnMapping(): ColumnMapping {
    columnMappingSequence += 1;
    return { id: `mapping-${columnMappingSequence}`, leftColumn: null, rightColumn: null };
  }

  get canAddCalculatedColumn(): boolean {
    return this.dataset.columnsLoaded && !this.dataset.columnsLoading &&
       !!this.calculatedColumnName.trim() && !!this.calculatedColumnExpression.trim() &&
       !isDuplicateColumnName(this.dataset.columns, this.calculatedColumnName) &&
       !this.calculatedColumnExpressionError &&
       !this.calculatedColumnSaving;  
  }

  onOpenAddCalculatedColumn(): void {
    this.calculatedColumnName = '';
    this.calculatedColumnExpression = '';
    this.calculatedColumnNameError = null;
    this.calculatedColumnExpressionError = null;
    this.calculatedColumnSaving = false;
    this.calculatedColumnPickerOpen = false;
    this.calculatedColumnSelectedKeys = new Set<string>();
    this.calculatedColumnInsertedKeys = new Set<string>();
    this.ensureDatasetColumnsLoaded(this.dataset);
    this.calculatedColumnModal?.show();
  }

  onCloseAddCalculatedColumn(): void {
    this.calculatedColumnModal?.hide();
  }

  onCalculatedColumnNameChange(): void {
    this.calculatedColumnNameError = isDuplicateColumnName(this.dataset.columns, this.calculatedColumnName)
      ? 'Column name is already available.'
      : null;
  }

  onCalculatedColumnExpressionChange(): void {
    this.calculatedColumnExpressionError = isDuplicateColumnAlias(this.dataset.columns, this.calculatedColumnExpression)
      ? 'Duplicate name or equivalent calculation'
      : validateCalculatedColumnExpression(this.calculatedColumnExpression, this.dataset.columns);
  }

  onToggleColumnPicker(): void {
    this.calculatedColumnPickerOpen = !this.calculatedColumnPickerOpen;
  }

  isColumnPicked(column: DatasetColumn): boolean {
    return this.calculatedColumnSelectedKeys.has(column.key);
  }

  onToggleColumnPick(column: DatasetColumn): void {
    if (this.calculatedColumnSelectedKeys.has(column.key)) {
      this.calculatedColumnSelectedKeys.delete(column.key);
    } else {
      this.calculatedColumnSelectedKeys.add(column.key);
    }
  }

  // Appends the picked column names (space-joined when more than one) to the expression so the user can keep editing.
  onInsertSelectedColumns(): void {
    const names = this.dataset.columns
      .filter(column => this.calculatedColumnSelectedKeys.has(column.key))
      .map(column => column.name);
    if (!names.length) {
      this.calculatedColumnPickerOpen = false;
      return;
    }
    const insertion = names.join(' ');
    const current = this.calculatedColumnExpression;
    this.calculatedColumnExpression = current && !current.endsWith(' ') ? `${current} ${insertion}` : `${current}${insertion}`;
    this.onCalculatedColumnExpressionChange();
    // Remember every column inserted so far (for the saved description) before clearing the checkboxes.
    this.calculatedColumnSelectedKeys.forEach(key => this.calculatedColumnInsertedKeys.add(key));
    this.calculatedColumnSelectedKeys = new Set<string>();
    this.calculatedColumnPickerOpen = false;
  }

  onAddCalculatedColumn(): void {
    if (!this.canAddCalculatedColumn) {
      return;
    }
    this.calculatedColumnSaving = true;
    const payload = {
      table_name: this.dataset.table_name,
      dataset_name: this.dataset.dataset_name,
      column_name: this.calculatedColumnName.trim(),
      alias: this.calculatedColumnExpression.trim(),
      table_description: this.getColumnDescription(),
      data_type: 'NVARCHAR(255)',
      DataType_Category: 'Text'
    };
    this.reportsService.addCalculatedColumn(payload).subscribe({
      next: (response: any) => {
        this.calculatedColumnSaving = false;
        const newColumn = mapApiColumn(response?.column ?? {
           ...payload,
           column_description: payload.table_description
         });
        this.dataset.columns = [...this.dataset.columns, newColumn];
        this.configChanged.emit();
        this.toastr.success('Calculated column added successfully', '', {
          timeOut: 5000,
          positionClass: 'toast-top-center'
        });
        this.onCloseAddCalculatedColumn();
      },
      error: () => {
        this.calculatedColumnSaving = false;
        this.toastr.error('Failed to add calculated column', '', {
          timeOut: 5000,
          positionClass: 'toast-top-center'
        });
      }
    });
  }

  getColumnDescription() :string {
    let description = 'Combination of the Unique ID of the ';
    if (!this.calculatedColumnInsertedKeys.size) {
      return '';
    }
    [...this.calculatedColumnInsertedKeys].forEach((key,index) => {
      if(index > 0) {
        if(index < this.calculatedColumnInsertedKeys.size - 1) {
          description += ' and ';
        }else{
          description += ' , ';
        }
      }
      description += key;
    });
    return description;
  }

  private ensureDatasetColumnsLoaded(target: DatasetConfig): void {
    this.loadDatasetColumns(target).subscribe();
  }

  // Emits once the dataset's columns are loaded (immediately if they already are, or the dataset has no table yet).
  private loadDatasetColumns(target: DatasetConfig): Observable<void> {
    if (!target.table_name || target.columnsLoaded) {
      return of(undefined);
    }
    const inFlight = columnLoads.get(target);
    if (inFlight) {
      return inFlight;
    }

    target.columnsLoading = true;
    const tableName = target.table_name;
    const request: Observable<void> = this.reportsService
      .getDatasourceColumns({ type: 'columnInfo', tableName, datasetName: target.dataset_name })
      .pipe(
        map((columns: any[]) => {
          // Ignore a response for a table the user has since replaced.
          if (target.table_name === tableName) {
          const savedColumns = new Map(target.columns.map(column => [column.key, column]));
          const hasSavedColumns = savedColumns.size > 0;
          target.columns = (columns || []).map(rawColumn => {
            const column = mapApiColumn(rawColumn);
            const savedColumn = savedColumns.get(column.key);
            return savedColumn
              ? {
                ...column,
                name: savedColumn.name || column.name,
                alias: savedColumn.alias || column.alias,
                type: savedColumn.type,
                selected: savedColumn.selected,
                aggregation: savedColumn.aggregation
              }
              : { ...column, selected: !hasSavedColumns };
          });
          target.columnsLoaded = true;
        }
      }),
        catchError(() => of(undefined)),
        finalize(() => {
          if (columnLoads.get(target) === request) {
            columnLoads.delete(target);
            target.columnsLoading = false;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    columnLoads.set(target, request);
    return request;
  }
}
